import {
  ClockIcon,
  LocationIcon,
  MailIcon,
  PaymentIcon,
  PeopleOutlineIcon,
} from "@/pages/taskpane/components/icons/IconCustom";
import HStack from "@components/layout/HStack";
import VStack from "@components/layout/VStack";
import Icon from "@components/ui/Icon";
import Status from "@components/ui/Status";
import { ResourceEntity } from "@features/resources/services/types/resourceEntity";
import { useThemeStore } from "@shared/stores";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { EStatusResource } from "../services/types/enum";
import { parseISOString } from "../ulti";
import IconPermission from "./IconPermission";
import { useFormatDateTime } from "@/pages/taskpane/shared/hooks";

type Props = {
  resource: ResourceEntity;
};

const richDescription = (rawHtml: string) => {
  return rawHtml ? (
    <div className="rf-resource-detail-row-rich-description">
      <div dangerouslySetInnerHTML={{ __html: rawHtml }} />
    </div>
  ) : null;
};

const ModalResourceDetail = ({ resource }: Props) => {
  const { t } = useTranslation();
  const { isDarkMode } = useThemeStore();
  const { handleFormatTimeWithLanguage } = useFormatDateTime();

  // const handleRenderStatus = () => {
  //   if (resource?.isApproved) {
  //     return (
  //       <ItemCommon
  //         icon={isDarkMode ? PersonAvailableDark : PersonAvailableIcon}
  //         textContent="Booking requires delegate approval. Your request will be fowarded"
  //       />
  //     );
  //   }
  //   if (resource?.isNotHavePermission) {
  //     return <ItemCommon icon={ProhibitIcon} textContent="You don’t have permission to book this resource" />;
  //   }
  //   if (resource?.isConflict) {
  //     return (
  //       <ItemCommon
  //         icon={WarningRed}
  //         textContent="You cannot book this resource because your meeting request conflicts with the resource's booking policies."
  //       />
  //     );
  //   }
  //   return null;
  // };

  const openingStartTime = useMemo(() => {
    if (!resource?.openStartTime) return "";
    const startTime = parseISOString(resource?.openStartTime);
    const startTimeString = `${startTime.year}-${startTime.month.toString().padStart(2, "0")}-${startTime.day
      .toString()
      .padStart(2, "0")} ${startTime.hours.toString().padStart(2, "0")}:${startTime.minutes
      .toString()
      .padStart(2, "0")}:${startTime.seconds.toString().padStart(2, "0")}`;
    return handleFormatTimeWithLanguage(startTimeString);
  }, [resource?.openStartTime, handleFormatTimeWithLanguage]);

  const openingEndTime = useMemo(() => {
    if (!resource?.openEndTime) return "";
    const endTime = parseISOString(resource?.openEndTime);
    const endTimeString = `${endTime.year}-${endTime.month.toString().padStart(2, "0")}-${endTime.day
      .toString()
      .padStart(2, "0")} ${endTime.hours.toString().padStart(2, "0")}:${endTime.minutes
      .toString()
      .padStart(2, "0")}:${endTime.seconds.toString().padStart(2, "0")}`;
    return handleFormatTimeWithLanguage(endTimeString);
  }, [resource?.openEndTime, handleFormatTimeWithLanguage]);

  const capacity = useMemo(() => {
    if (resource.isPooledResource) {
      return resource?.availability?.allowCapacity || 0;
    } else {
      return resource.capacity || 0;
    }
  }, [resource]);

  return (
    <VStack gap={4} className="text-sm px-5 py-4">
      <HStack className="w-full" alignItems="center" gap={2}>
        <PeopleOutlineIcon isDarkMode={isDarkMode} />
        <HStack justifyContent="between" alignItems="center" className="w-full flex-1">
          <p className="font-semibold">{capacity}</p>
          <Status
            status={resource?.availability?.isAvailable ? EStatusResource.Available : EStatusResource.Unavailable}
            isUseIcon={false}
          />
        </HStack>
      </HStack>

      <ItemCommon icon={<LocationIcon isDarkMode={isDarkMode} />} textContent={resource?.location || ""} />

      {/* {handleRenderStatus()} */}
      <IconPermission resource={resource} isNoTooltip={true} isVertical={true} fontSizeClassname="text-sm" />

      {(openingStartTime || openingEndTime || resource?.preBufferTime) ? (
        <ItemCommon
          icon={<ClockIcon isDarkMode={isDarkMode} />}
          children={
            <>
              {openingStartTime && openingEndTime ? (
                <p>
                  {t("Opening hours:")} {openingStartTime} - {openingEndTime}
                </p>
              ) : null}
              {resource?.preBufferTime ? (
                <p>
                  {t("Buffer time {{minutes}} mins between reservations", { minutes: resource?.preBufferTime || 0 })}
                </p>
              ) : null}
            </>
          }
        />
      ) : null}

      {resource?.costInitial || resource?.costPrMinute ? (
        <ItemCommon
          icon={<PaymentIcon isDarkMode={isDarkMode} />}
          textContent={t("Initial payment {{value}} pr.minute", {
            value: `${resource?.costInitial || 0} + ${resource?.costPrMinute || 0}`,
          })}
        />
      ) : null}

      {resource?.categories?.length ? (
        <VStack className="rounded px-3 py-2 bg-extraLightGray dark:bg-deepGray" gap={3}>
          {resource?.categories?.map((category) => (
            <HStack key={category.id} className="w-full text-sm" alignItems="start" gap={2}>
              {category.iconUrl ? <Icon src={category.iconUrl} alt="icon category" className="w-4 h-4" /> : null}
              <div className="font-semibold flex-1">{category.caption}</div>
            </HStack>
          ))}
        </VStack>
      ) : null}

      <p>{resource?.description || ""}</p>
      {resource?.richDescription ? richDescription(resource?.richDescription) : null}
      {resource?.imageUrl ? <img src={resource.imageUrl} alt="resource" className="w-full rounded" /> : null}

      <ItemCommon icon={<MailIcon isDarkMode={isDarkMode} />} textContent={resource?.email} />
    </VStack>
  );
};

export default ModalResourceDetail;

type ItemCommonProps = {
  icon?: React.ReactNode;
  textContent?: string | number;
  children?: React.ReactNode;
};

const ItemCommon = ({ icon, textContent, children }: ItemCommonProps) => {
  return (
    <HStack className="w-full text-sm" alignItems="start" gap={2}>
      {icon ? icon : null}
      <div className="font-semibold flex-1">{children ? children : <p className="break-all">{textContent}</p>}</div>
    </HStack>
  );
};
